import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePartnersReadAcquisition } from '@/api/hooks';
import LoadErrorState from '@/components/LoadErrorState';
import AcquisitionDailyChart from '@/domain/acquisition/components/AcquisitionDailyChart';
import AcquisitionEmptyState from '@/domain/acquisition/components/AcquisitionEmptyState';
import AcquisitionIndicatorsStrip from '@/domain/acquisition/components/AcquisitionIndicatorsStrip';
import AcquisitionLinkCard from '@/domain/acquisition/components/AcquisitionLinkCard';
import AcquisitionSkeleton from '@/domain/acquisition/components/AcquisitionSkeleton';
import CommissionSummaryCard from '@/domain/acquisition/components/CommissionSummaryCard';
import { type AcquisitionPeriodDays } from '@/domain/acquisition/types';
import {
  ACQUISITION_PERIODS,
  getAcquisitionIndicators,
  getCommissionSummary,
  parseAcquisitionPeriod,
  toAcquisitionParams,
} from '@/domain/acquisition/utils';
import { useTranslate } from '@/state/i18n';

/**
 * `/links` ("Seus links"): the tracked links the Bodyprint team made for the partner, what they
 * brought in the window (`?days=`) and the commissions. Aggregates only, never who signed up.
 */
const AcquisitionLinks = () => {
  const t = useTranslate('domain.acquisition.AcquisitionLinks');
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseAcquisitionPeriod(searchParams.get('days'));
  const params = useMemo(() => toAcquisitionParams(days), [days]);
  const { data, isLoading, isError, refetch } = usePartnersReadAcquisition(params);
  const hasLinks = Boolean(data?.links.length);

  const renderContent = () => {
    if (isLoading) return <AcquisitionSkeleton />;
    if (isError || !data) {
      return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
    }
    if (!data.links.length) return <AcquisitionEmptyState />;
    return (
      <Stack gap={3}>
        <AcquisitionIndicatorsStrip indicators={getAcquisitionIndicators(data.totals)} />
        <Box sx={sx.columns}>
          <AcquisitionDailyChart series={data.series} />
          <CommissionSummaryCard summary={getCommissionSummary(data.commissions)} />
        </Box>
        <Stack gap={1.5}>
          <Typography variant="h3" component="h2">
            {t('linksTitle', { count: data.links.length })}
          </Typography>
          <Box sx={sx.links}>
            {data.links.map((link) => (
              <AcquisitionLinkCard key={link.id} link={link} />
            ))}
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack gap={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
      >
        <Stack gap={0.5}>
          <Typography component="h1" variant="h2">
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
        </Stack>
        {hasLinks ? (
          <TextField
            select
            size="small"
            value={days}
            slotProps={{ select: { inputProps: { 'aria-label': t('period') } } }}
            onChange={(event) =>
              setSearchParams(
                { days: String(Number(event.target.value) as AcquisitionPeriodDays) },
                { replace: true },
              )
            }
            sx={{ minWidth: 180 }}
          >
            {ACQUISITION_PERIODS.map((option) => (
              <MenuItem key={option} value={option}>
                {t('lastDays', { count: option })}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>
      {renderContent()}
    </Stack>
  );
};

export default AcquisitionLinks;

const sx = {
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
  },
  links: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
  },
};
